import { Field, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { POSITIONS } from '@hivebrite/design-system/Tooltip';

import ReactSelectSearchAndOptions from 'hb-react/shared/components/ReactSelect/ReactSelectSearchAndOptions';
import { getOptionValue } from 'hb-react/shared/components/ReactSelect/utils/selectValues';

const SelectSearchAndOptions = ({
  ariaLabel,
  disabled,
  id,
  inputHtml,
  isClearable,
  isNotSearchable,
  label,
  labelMultiline,
  name,
  hideError,
  noOptionsMessage,
  notCloseMenuOnSelect,
  onBlur,
  onChange,
  options,
  placeholder,
  showValueAsPlaceholder,
  shouldResetFormikSearchValueOnOtherOptionSelection,
  tooltip,
  validate: { required },
  wrapperProps,
}) => {
  const { errors, values, setFieldValue, setFieldTouched } = useFormikContext();
  const theme = useTheme();
  const htmlId = id || name;
  const formikValue = getIn(values, name);
  const formikError = getIn(errors, name);

  const [inputValue, setInputValue] = useState('');

  const handleChange = (selectedOption, { action }) => {
    if (action === 'select-option') {
      const groupOfOptions = options.find((groupOfOptions) => {
        if (groupOfOptions?.options) {
          return groupOfOptions.options.find(
            (options) => options.id === getOptionValue(selectedOption),
          );
        }

        return null;
      });

      if (selectedOption?.type === 'search') {
        if (groupOfOptions?.shouldUpdateFormikValues) {
          setFieldValue(name, inputValue);
        }

        if (onChange) {
          onChange(inputValue);
        }

        return;
      }

      if (groupOfOptions) {
        if (groupOfOptions?.shouldUpdateFormikValues) {
          setFieldValue(groupOfOptions.name, getOptionValue(selectedOption));
        }

        if (groupOfOptions.onChange) {
          groupOfOptions.onChange({
            groupOfOptions,
            data: selectedOption,
          });
        }
      }
    }
    if (action === 'clear') {
      setInputValue('');

      // How to handle shouldUpdateFormikValues here?
      setFieldValue(name, '');

      if (onChange) {
        onChange('');
      }
    }
  };

  const handleSearchChange = (inputValue) => {
    setInputValue(inputValue);

    setFieldValue(name, inputValue);

    if (onChange) {
      onChange(inputValue);
    }
  };

  const handleOtherOptionsChange = ({ groupOfOptions, data }) => {
    if (groupOfOptions) {
      if (groupOfOptions?.shouldUpdateFormikValues) {
        setFieldValue(groupOfOptions.name, data.id);
      }

      if (groupOfOptions.onChange) {
        groupOfOptions.onChange({
          groupOfOptions,
          data,
        });
      }
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);

    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Box
      mb={6}
      display="block"
      opacity={disabled ? theme.input.disabled.opacity : 1}
      {...wrapperProps}
    >
      {label && (
        <Label
          id={htmlId}
          disabled={disabled}
          multiline={labelMultiline}
          required={required}
          tooltip={tooltip}
          initialPosition={POSITIONS.BOTTOM}
        >
          {label}
        </Label>
      )}

      <Field name={name}>
        {() => (
          <ReactSelectSearchAndOptions
            ariaLabel={ariaLabel}
            disabled={disabled}
            id={htmlId}
            isClearable={isClearable}
            isNotSearchable={isNotSearchable}
            name={name}
            noOptionsMessage={noOptionsMessage}
            notCloseMenuOnSelect={notCloseMenuOnSelect}
            onBlur={handleBlur}
            onChange={handleChange}
            onInnerInputChange={handleSearchChange}
            onOtherOptionsChange={handleOtherOptionsChange}
            onSearchChange={handleSearchChange}
            options={options}
            placeholder={placeholder}
            showValueAsPlaceholder={showValueAsPlaceholder}
            shouldResetFormikSearchValueOnOtherOptionSelection={
              shouldResetFormikSearchValueOnOtherOptionSelection
            }
            value={formikValue}
            {...inputHtml}
          />
        )}
      </Field>
      {!hideError && formikError && (
        <AdditionalInformation type="error" id={htmlId}>
          {formikError}
        </AdditionalInformation>
      )}
    </Box>
  );
};

SelectSearchAndOptions.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  getOptionValue: PropTypes.func,
  id: PropTypes.string,
  inputHtml: PropTypes.object,
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  label: PropTypes.string,
  labelAfter: PropTypes.bool,
  labelHtml: PropTypes.object,
  notCloseMenuOnSelect: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  showValueAsPlaceholder: PropTypes.bool,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
  ariaLabel: PropTypes.string,
};

SelectSearchAndOptions.defaultProps = {
  getOptionValue,
  inputHtml: {},
  showValueAsPlaceholder: false,
  shouldResetFormikSearchValueOnOtherOptionSelection: false,
  type: 'default',
  validate: {},
  wrapperProps: {},
};

export default SelectSearchAndOptions;
